import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Tab,
} from '@mui/material';
import {
  ConditionGroup,
  FieldAttributes,
  FieldTemplate,
  FieldTemplateMappings,
} from 'api/models/FieldTemplate';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  dialogState,
  fieldTemplateAliases,
  fieldTemplatesState,
  formTemplateState,
  layoutSelector,
} from 'state';
import { removeEmpty } from 'utils';
import {
  getDefaultFormFieldValues,
  getFormFieldValidationSchema,
  getIsDisabledSaveButton,
} from './forms/utils';
import { produce } from 'immer';
import { del } from 'object-path';
import { getFieldAlias } from 'api';
import { useParams } from 'react-router-dom';
import { GeneralIds, ModelTypes } from 'types/types';
import { useSaveFormTemplate } from 'hooks/useSaveFormTemplate';
import { useState } from 'react';
import { TabContext, TabList } from '@mui/lab';
import FieldConfigTab from './components/FieldConfigTab';
import { mutation, query } from 'operations';
import ConditionsConfigTab from './components/ConditionsConfigTab';
import { CallSplit, Input, VisibilityOff } from '@mui/icons-material';
import { FormTemplateStatus } from 'api/models/FormTemplate';
import CorrespondingTab from './components/CorrespondingTab';

export type AddFieldValues = FieldAttributes & {
  alias: string;
  isRequiredConditions?: ConditionGroup;
  isVisibleConditions?: ConditionGroup;
  mappings?: FieldTemplateMappings;
};

export function AddFieldTemplateDialog() {
  const [
    {
      data: { fieldTemplate: selectedField, path },
    },
    closeDialog,
  ] = useRecoilState(dialogState);
  const { t } = useTranslation();
  const [layout, setLayout] = useRecoilState(layoutSelector);
  const [templates, setTemplates] = useRecoilState(fieldTemplatesState);
  const { projectId } = useParams<GeneralIds>();

  const usedAliases = useRecoilValue(fieldTemplateAliases);
  const { save: autosaveFormTemplate } = useSaveFormTemplate({
    showToast: false,
  });
  const [activeTab, setActiveTab] = useState('1');
  const formTemplate = useRecoilValue(formTemplateState);
  // if URL contains /view
  const isViewInUrl = window.location.href.includes('/view');

  const disabled =
    formTemplate?.status === FormTemplateStatus.Published || isViewInUrl;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  const onClose = () => {
    const isEditing = selectedField.fieldAttributes.label ? true : false;
    if (!isEditing) {
      setLayout(
        produce(layout, draft => {
          del(draft, path);
        }),
      );
      setTemplates(templates.filter(t => t.id !== selectedField.id));
    }
    closeDialog({
      isOpen: false,
    });
  };

  const formik = useFormik<AddFieldValues>({
    initialValues: getDefaultFormFieldValues(selectedField),
    validationSchema: getFormFieldValidationSchema(selectedField, t),
    validate() {},
    onSubmit: async (values, { resetForm }) => {
      // referedFieldsOptions are used only in selects, in a select editor other selects can be updated
      const referedFieldsOptions = values.referedFieldsOptions ?? [];
      const fieldAttributes = {
        ...removeEmpty({
          ...{
            ...values,
          },
          alias: null,
          conditionGroups: null,
          isRequiredConditions: null,
          isVisibleConditions: null,
        }),
      };

      const newFieldTemplate = {
        ...selectedField,
        alias: values.alias,
        fieldAttributes,
        isRequired: values.isRequiredConditions,
        isVisible: values.isVisibleConditions,
        mappings: values.mappings,
      };

      // if there is no alias we generate one
      if (!values.alias) {
        const alias = await getFieldAlias({
          projectId,
          title: formik.values.label,
          usedAliases: usedAliases,
        });
        newFieldTemplate.alias = alias;
      }

      const updatedTemplates = templates.map(t => {
        if (t.id === selectedField.id) {
          return newFieldTemplate;
        }
        if (
          t.id === values.optionsSourceFieldTemplateId &&
          referedFieldsOptions.length > 0
        ) {
          return {
            ...t,
            fieldAttributes: {
              ...t.fieldAttributes,
              options: referedFieldsOptions,
            },
          };
        }
        return t;
      });

      await autosaveFormTemplate({
        items: layout.children,
        templates: updatedTemplates,
      });

      // this is needed to update the code options of a select field if a user updated them
      if (
        values.optionsSourceFieldTemplateId &&
        (values.referedFieldsOptions?.length ?? 0) > 0
      ) {
        const { data: referedFormTemplates } = await query<FieldTemplate[]>({
          type: ModelTypes.fieldTemplate,
          ids: {
            projectId,
            id: values.optionsSourceFieldTemplateId,
          },
        });
        const referedSelect = referedFormTemplates?.[0];
        if (referedSelect) {
          referedSelect.fieldAttributes.options = referedFieldsOptions;
        }

        await mutation<FieldTemplate>({
          type: ModelTypes.fieldTemplate,
          ids: {
            projectId,
            id: values.optionsSourceFieldTemplateId,
          },
          body: {
            ...referedSelect,
            // the following props are filled out by the backend, we can't send them back
            formTemplateId: undefined,
            formTemplateTypeId: undefined,
            projectId: undefined,
            createdAt: undefined,
            createdBy: undefined,
            updatedAt: undefined,
            updatedBy: undefined,
            __v: undefined,
          },
        });
      }

      resetForm();

      closeDialog({
        isOpen: false,
      });
    },
  });

  return (
    <Box>
      <DialogTitle>{`${t(`forms.fieldType.${selectedField?.fieldType}`)} ${t(
        'dialogs.addField.title',
      )}`}</DialogTitle>
      <DialogContent>
        <TabContext value={activeTab}>
          <TabList onChange={handleTabChange}>
            <Tab
              label={t('dialogs.addField.configTab')}
              value="1"
              icon={<Input />}
            />
            <Tab
              label={t('dialogs.addField.visibilityTab')}
              value="2"
              icon={<VisibilityOff />}
            />
            <Tab
              label={t('dialogs.addField.correspondingTab')}
              value="3"
              icon={<CallSplit />}
            />
          </TabList>
          {activeTab === '1' && (
            <FieldConfigTab
              field={selectedField}
              formik={formik}
              disabled={disabled}
            />
          )}
          {activeTab === '2' && (
            <ConditionsConfigTab formik={formik} disabled={disabled} />
          )}
          {activeTab === '3' && (
            <CorrespondingTab
              formik={formik}
              disabled={disabled}
              fieldType={selectedField?.fieldType}
            />
          )}
        </TabContext>
      </DialogContent>
      <DialogActions sx={{ pt: 2 }}>
        <Button
          onClick={onClose}
          color="secondary"
          variant="contained"
          sx={{ mb: 1 }}
          disabled={formik.isSubmitting}
        >
          {t('general.cancel')}
        </Button>
        <Button
          type="submit"
          color="primary"
          variant="contained"
          onClick={formik.submitForm}
          sx={{ mr: 2, mb: 1 }}
          disabled={getIsDisabledSaveButton({
            isSubmitting: formik.isSubmitting,
            label: formik.values.label,
            alias: formik.values.alias,
            disabled,
            isValid: formik.isValid,
          })}
        >
          {formik.isSubmitting ? t('general.isSaving') : t('general.save')}
        </Button>
      </DialogActions>
    </Box>
  );
}
